/**
 * 1.81.0 · Puesto: conexión con el servidor de la sucursal. Mantiene la conexión (reintenta sola y, si
 * el servidor cambió de IP, lo vuelve a buscar en la red por su id), avisa los cambios de tablas a la
 * pantalla y le da la conexión a la base remota (remote-db.ts).
 */
import { LanClient, LanError, lanErrorText, type PairedDevice, type Pairing, type Wire } from "./channel";
import { lanNative, onLanEvent, type FoundServer } from "./native";
import { loadPairing, readLanConfig, saveDevices, savePairing, writeLanConfig, type LanConfig } from "./role";
import { env } from "@/lib/env";
import { platform } from "@/platform";
import { deviceState } from "@/modules/devices/device-state";
import { compareVersions } from "@/platform/version";
import { requestUpdateNow } from "@/app/update-now";
import { lanSchema } from "./schema-sig";
import { lanTag } from "./crypto";

export type PuestoState =
  | { state: "idle" }
  | { state: "unpaired" }
  | { state: "connecting"; attempt: number }
  | { state: "ready"; server: string }
  | { state: "lost"; error: string; attempt: number; since: number }
  | { state: "error"; error: string; code?: string };

/**
 * Lo que publica el servidor en la búsqueda. 1.99.5: solo su id, el arranque, el protocolo y huellas del negocio
 * y la sucursal (`bh`, `lh`): ni nombres ni versión. Los servidores de antes mandan `biz`, `name`, `v`…
 */
export interface ServerInfo { id: string; inst?: string; pv?: number; bh?: string; lh?: string; gen?: number;
  name?: string; biz?: string; loc?: string | null; v?: string; bizName?: string; locName?: string }

let st: PuestoState = { state: "idle" };
const subs = new Set<(s: PuestoState) => void>();
const set = (s: PuestoState) => { st = s; subs.forEach((f) => f(s)); };
export const puestoState = {
  get: () => st,
  subscribe(f: (s: PuestoState) => void) { subs.add(f); return () => { subs.delete(f); }; },
};

// Cambios de tablas que avisa el servidor (las pantallas vuelven a leer).
const changeSubs = new Set<(tables: string[] | null) => void>();
export function onTablesChanged(f: (tables: string[] | null) => void) { changeSubs.add(f); return () => { changeSubs.delete(f); }; }
const emitChanged = (t: string[] | null) => changeSubs.forEach((f) => f(t));

/** 1.99.1: resultados de las consultas en vivo que manda el servidor (por id de suscripción). */
export type LiveMsg = { s: string; v: number; rows?: unknown[]; e?: string };
const liveSubs = new Map<string, (m: LiveMsg) => void>();
export function onLive(sid: string, f: (m: LiveMsg) => void): () => void {
  liveSubs.set(sid, f);
  return () => { if (liveSubs.get(sid) === f) liveSubs.delete(sid); };
}

let client: LanClient | null = null;
let waiters: Array<{ ok: (c: LanClient) => void; ko: (e: Error) => void }> = [];
let loop: ReturnType<typeof setTimeout> | null = null;
let attempt = 0;
let lostSince = 0;
let started = false;

// 1.81.7: diferencia entre la hora de esta PC y la del servidor (todos usan la del servidor).
let skew = 0;
const skewSubs = new Set<(ms: number) => void>();
export const clockSkew = {
  get: () => skew,
  subscribe(f: (ms: number) => void) { skewSubs.add(f); return () => { skewSubs.delete(f); }; },
};
const RealDate = Date;
/** Hora del servidor (la de esta PC corregida por la diferencia medida al conectar). */
export const serverNow = () => new RealDate(RealDate.now() + skew);
/**
 * 1.81.16 · Si el reloj de esta PC está corrido más de 2 minutos, la app usa la hora del servidor: todo lo que
 * se registra (ventas, caja, pedidos) sale con la hora del servidor. Solo en los puestos.
 */
let shimmed = false;
function applyServerClock() {
  if (shimmed) return;
  shimmed = true;
  const Shim = new Proxy(RealDate, {
    construct(t, args: unknown[]) { return args.length ? Reflect.construct(t, args) : new t(t.now() + skew); },
    apply(t) { return new t(t.now() + skew).toString(); },
    get(t, p, r) { return p === "now" ? () => t.now() + skew : Reflect.get(t, p, r); },
  });
  (globalThis as { Date: DateConstructor }).Date = Shim;
}
async function measureClock(c: LanClient) {
  try {
    const t0 = RealDate.now();
    const r = await c.call<{ now: string }>("time", {});
    const t1 = RealDate.now();
    const srv = RealDate.parse(r?.now ?? "");
    if (!Number.isFinite(srv)) return;
    skew = Math.round(srv - (t0 + t1) / 2);
    if (Math.abs(skew) >= 120_000) applyServerClock();
    skewSubs.forEach((f) => f(skew));
  } catch { /* servidor viejo sin «time» */ }
}

export function parseInfo(raw: string): ServerInfo | null {
  try {
    const x = JSON.parse(raw) as ServerInfo;
    return x && typeof x.id === "string" && (typeof x.bh === "string" || typeof x.biz === "string") ? x : null;
  } catch { return null; }
}

/** Busca servidores de este negocio en la red. */
export async function findServers(ms = 1500): Promise<Array<FoundServer & { parsed: ServerInfo }>> {
  const biz = deviceState.get()?.businessId;
  const bh = biz ? await lanTag("biz", biz) : null;
  const list = await lanNative.discover(ms);
  return list.flatMap((f) => {
    const parsed = parseInfo(f.info);
    return parsed && (!biz || parsed.bh === bh || parsed.biz === biz) ? [{ ...f, parsed }] : [];
  });
}

/** Conexión lista (espera hasta 30 s a que vuelva; si no, falla y la pantalla muestra «Sin servidor»). */
export function getClient(): Promise<LanClient> {
  if (client?.ready) return Promise.resolve(client);
  return new Promise<LanClient>((ok, ko) => {
    const w = { ok, ko };
    waiters.push(w);
    setTimeout(() => {
      if (!waiters.includes(w)) return;
      waiters = waiters.filter((x) => x !== w);
      ko(new LanError("closed", "Sin conexión con el servidor"));
    }, 30_000);
  });
}

function nativeWire(): Wire {
  return { send: (t) => lanNative.clientSend(t), close: () => void lanNative.clientClose().catch(() => undefined) };
}

let routed = false;
function route() {
  if (routed) return;
  routed = true;
  onLanEvent((e) => {
    if (e.kind === "client_msg") void client?.receive(e.data);
    else if (e.kind === "client_closed") client?.gone();
  });
}

/** Una conexión (sin reintentos). Con `pair`, vincula este puesto. */
async function connectOnce(host: string, port: number, pairing: Pairing | null, pair?: { mode: "ask" } | { mode: "code"; code: string }, onSas?: (s: string) => void,
  confirmSas?: (s: string) => Promise<boolean>): Promise<LanClient> {
  route();
  const dev = deviceState.get();
  if (!dev) throw new LanError("business", "Este equipo no está registrado en el negocio");
  client?.close();
  await lanNative.connect(host, port);
  const c = new LanClient(nativeWire(), {
    version: env.version,
    schema: lanSchema(),
    name: await platform.deviceName().catch(() => "Puesto"),
    businessId: dev.businessId,
    pairing,
    pair,
    onSas,
    confirmSas,
    onPaired: async (p) => { await savePairing(p); },
    onEvent: (ev, d) => {
      if (ev === "ch") emitChanged(Array.isArray(d) ? (d as string[]) : null);
      else if (ev === "bk") {
        // 1.99.2: copia de lo que el servidor no subió: se guarda cifrada y se le avisa que quedó.
        const copy = d as import("./backup").BackupCopy;
        void import("./backup").then((b) => b.storeCopy(copy)).then((ok) => ok, () => false)
          .then((ok) => c.call("bk_ok", { at: copy?.at, ok }, 10_000)).catch(() => undefined);
      }
      else if (ev === "bkid") {
        // 1.99.11: la identidad del servidor (para reemplazarlo si se rompe): solo la del servidor con el que está vinculado.
        const x = d as import("./backup").ServerIdentityCopy | null;
        void loadPairing().then((p) => p && x && x.srv === p.serverId ? import("./backup").then((b) => b.storeIdentity(x)) : false).catch(() => undefined);
      }
      else if (ev === "bkc") {
        // 1.99.19: los contadores del servidor (para seguir la numeración si esta PC lo reemplaza).
        const x = d as { srv?: string; counters?: import("./backup").CounterRow[] } | null;
        void loadPairing().then((p) => p && x?.srv === p.serverId && Array.isArray(x.counters) ? import("./backup").then((b) => b.storeCounters(x.srv!, x.counters!)) : undefined).catch(() => undefined);
      }
      else if (ev === "lv") { const m = d as LiveMsg | null; if (m && typeof m.s === "string") liveSubs.get(m.s)?.(m); }
      else if (ev === "takeover") void takeOver(d);
    },
    // 1.98.0: el puesto cortó la conexión porque el servidor no contesta (latido): «Sin servidor» y reintenta.
    onClosed: () => {
      if (client !== c || readLanConfig().role !== "puesto") return;
      client = null;
      if (!lostSince) lostSince = Date.now();
      set({ state: "lost", error: lanErrorText("closed"), attempt, since: lostSince });
      if (!loop) loop = setTimeout(() => void tick(), 500);
    },
  });
  client = c;
  try {
    await c.start();
  } catch (e) {
    if (client === c) client = null;
    c.close();
    throw e;
  }
  return c;
}

let updateAsked = false;
function ready(c: LanClient) {
  // 1.99.5: el servidor tiene una versión más nueva pero compatible: se trabaja igual y este puesto se actualiza.
  const sv = c.server?.v;
  if (sv && !updateAsked && compareVersions(sv, env.version) > 0) { updateAsked = true; requestUpdateNow(`el servidor tiene la ${sv}`); }
  attempt = 0;
  lostSince = 0;
  set({ state: "ready", server: c.server?.name ?? "servidor" });
  const w = waiters; waiters = [];
  w.forEach((x) => x.ok(c));
  emitChanged(null); // todo lo que se muestra se vuelve a leer
  void measureClock(c);
}

/** 1.99.15: veces seguidas que el servidor de siempre dijo «no conozco a este puesto». */
let unknownFrom = 0;

/** 1.99.19: mientras se reemplaza al servidor, los reintentos esperan (usan la misma conexión que la prueba). */
let replacing = false;
let tickRun: Promise<void> | null = null;

async function tick() {
  if (replacing) { loop = null; return; }
  tickRun = tickNow();
  try { await tickRun; } finally { tickRun = null; }
}

async function tickNow() {
  loop = null;
  const cfg = readLanConfig();
  const pairing = await loadPairing();
  if (cfg.role !== "puesto") return;
  if (!pairing || !cfg.server) { set({ state: "unpaired" }); return; }
  attempt++;
  if (st.state !== "lost") set({ state: "connecting", attempt });
  let host = cfg.server.host;
  let port = cfg.server.port;
  try {
    // Desde el segundo intento (1.98.0; antes cada tres): ¿el servidor cambió de IP? Se lo busca en la red por su id.
    if (attempt >= 2) {
      // 1.99.11: si hay dos con el mismo id (uno reemplazó al otro), el de la generación más nueva.
      // 1.99.15: la generación viaja sin firmar (cualquiera en la red puede anunciar una más alta): se alterna entre la
      // dirección que ya anduvo y lo que se encontró, y la dirección nueva se guarda recién cuando la conexión anda.
      const found = (await findServers(1200).catch(() => [])).filter((f) => f.parsed.id === pairing.serverId)
        .sort((a, b) => Number(b.parsed.gen ?? 0) - Number(a.parsed.gen ?? 0));
      const known = cfg.server;
      const other = found.find((f) => f.ip !== known.host || f.port !== known.port);
      const same = found.some((f) => f.ip === known.host && f.port === known.port);
      if (other && (!same || attempt % 2 === 0)) { host = other.ip; port = other.port; }
    }
    const c = await connectOnce(host, port, pairing);
    unknownFrom = 0;
    c.server && writeLanConfig({ ...readLanConfig(), server: { host, port, id: pairing.serverId, name: c.server.name } });
    ready(c);
  } catch (e) {
    const err = e as LanError;
    // 1.99.15: «no te conozco» se cree recién si lo dice tres veces seguidas la dirección donde ya anduvo (antes
    // alcanzaba una respuesta de cualquier equipo de la red para desvincular el puesto).
    if (err.code === "unknown" && host === cfg.server.host && port === cfg.server.port) {
      if (++unknownFrom >= 3) {
        unknownFrom = 0;
        await import("./backup").then((b) => b.clearIdentity(pairing.serverId)).catch(() => undefined);
        await savePairing(null); set({ state: "unpaired" }); return;
      }
    } else if (err.code !== "unknown") unknownFrom = 0;
    if (err.code === "business") { set({ state: "error", error: err.message, code: err.code }); return; }
    if (!lostSince) lostSince = Date.now();
    let message = err.message || lanErrorText("closed");
    // 1.81.7: versiones distintas: el que tiene la vieja se actualiza al instante.
    if (err.code === "version") {
      const srv = String(err.extra?.v ?? "");
      if (srv && compareVersions(srv, env.version) > 0) { requestUpdateNow(`el servidor tiene la ${srv}`); message = `El servidor tiene la versión ${srv} y este puesto la ${env.version}: este puesto se está actualizando solo.`; }
      else if (srv) message = `Este puesto tiene la versión ${env.version} y el servidor la ${srv}: el servidor se está actualizando solo. En cuanto termine, este puesto se conecta.`;
    }
    set({ state: "lost", error: message, attempt, since: lostSince });
    loop = setTimeout(() => void tick(), err.code === "version" ? 15_000 : Math.min(10_000, 1000 * attempt));
  }
}

/** Arranca (una vez) la conexión del puesto y la mantiene. */
export function startPuesto() {
  if (started) return;
  started = true;
  route();
  onLanEvent((e) => {
    if (e.kind !== "client_closed" || readLanConfig().role !== "puesto") return;
    if (!lostSince) lostSince = Date.now();
    set({ state: "lost", error: lanErrorText("closed"), attempt, since: lostSince });
    if (!loop) loop = setTimeout(() => void tick(), 500);
  });
  void tick();
}

/**
 * 1.81.10 · El servidor le pasa el lugar a este puesto: queda como servidor con la misma identidad (id y
 * puestos vinculados) y se reinicia (baja la base de la nube). Llega por la conexión cifrada y vinculada.
 */
async function takeOver(d: unknown) {
  const x = d as { serverId?: string; port?: number; devices?: PairedDevice[]; backupDeviceId?: string | null; gen?: number; counters?: import("./backup").CounterRow[] } | null;
  if (!x?.serverId || !Array.isArray(x.devices)) return;
  const pairing = await loadPairing();
  if (pairing?.serverId !== x.serverId) return; // solo el servidor con el que está vinculado
  set({ state: "error", error: "Esta PC pasa a ser el servidor de la sucursal: se reinicia y baja la base de la nube…", code: "takeover" });
  await saveDevices(x.devices.filter((v) => v && typeof v.id === "string" && typeof v.key === "string"));
  // 1.99.19: con la generación del que pasa el lugar (si era un reemplazo, el nuevo no vuelve a 0 y empata con el viejo)
  // y sus contadores (sigue la numeración).
  writeLanConfig({ role: "server", serverId: x.serverId, port: Number(x.port) || 47810, backupDeviceId: typeof x.backupDeviceId === "string" ? x.backupDeviceId : null,
    gen: Math.max(0, Math.floor(Number(x.gen) || 0)) });
  if (Array.isArray(x.counters)) await import("./backup").then((b) => b.storeCounters(x.serverId!, x.counters!)).catch(() => undefined);
  // El pase exige que el servidor viejo no tenga nada sin subir: la copia que guardaba esta PC ya no hace falta.
  await import("./backup").then(async (b) => { await b.clearCopy(x.serverId!); await b.clearIdentity(x.serverId!); }).catch(() => undefined);
  await savePairing(null);
  await import("@/modules/push/push-desktop").then((m) => m.setDesktopAutostart(true)).catch(() => undefined);
  client?.close();
  setTimeout(() => window.location.reload(), 800);
}

/**
 * 1.99.11 · El servidor se rompió: esta PC (el puesto de respaldo, que tiene su identidad) pasa a ser el servidor
 * con el mismo id y los mismos puestos vinculados (los demás la encuentran solos en la red). Necesita internet:
 * baja la base de la nube y la nube controla que el servidor viejo no se haya conectado hace poco (y le deja la
 * orden de dejar de ser servidor). Lo que no llegó a subir se carga después con «Restaurar» (la copia se conserva).
 */
export async function canReplaceServer(): Promise<boolean> {
  const p = await loadPairing();
  if (!p) return false;
  const { loadIdentity } = await import("./backup");
  return !!(await loadIdentity(p.serverId))?.devices.length;
}

/**
 * `admin`: email y contraseña de un administrador (se comprueban en la nube aparte de la sesión de esta PC, que
 * puede ser de otra persona: el reemplazo le da a esta PC las llaves de todos los puestos).
 */
export async function replaceServer(admin: { email: string; password: string }): Promise<void> {
  try {
    await replaceServerNow(admin);
  } catch (e) {
    // No se reemplazó: el puesto vuelve a buscar a su servidor.
    if (replacing) { replacing = false; if (!loop) loop = setTimeout(() => void tick(), 500); }
    throw e;
  }
}

async function replaceServerNow(admin: { email: string; password: string }): Promise<void> {
  const pairing = await loadPairing();
  const dev = deviceState.get();
  if (!pairing || !dev) throw new Error("Este equipo no es un puesto vinculado");
  if (!platform.secrets) throw new Error("Esta PC no puede guardar las llaves de los puestos");
  const { loadIdentity, clearIdentity } = await import("./backup");
  const ident = await loadIdentity(pairing.serverId);
  if (!ident?.devices.length) throw new Error("Esta PC no tiene los datos del servidor: el reemplazo se hace desde el puesto de respaldo");
  if (typeof navigator !== "undefined" && navigator.onLine === false) throw new Error("Hace falta internet: esta PC baja la base de la nube");
  // El servidor no tiene que estar contestando en la red (si contesta, no está roto: es la red de este puesto).
  if ((await findServers(2500).catch(() => [])).some((f) => f.parsed.id === pairing.serverId)) {
    throw new Error("El servidor contesta en la red: no está roto. Tocá «Reintentar ahora» o revisá el cable o el Wi-Fi de este puesto.");
  }
  // 1.99.19: la búsqueda puede no verlo (la red bloquea los avisos): se prueba también su dirección conocida, con los
  // reintentos del puesto parados (usan la misma conexión).
  // Si algo falla de acá en adelante, `replaceServer` vuelve a arrancar los reintentos.
  replacing = true;
  if (loop) { clearTimeout(loop); loop = null; }
  await (tickRun ?? Promise.resolve()).catch(() => undefined);
  const alive = await answersAt(readLanConfig().server);
  if (alive === true) throw new Error("El servidor contesta en su dirección: no está roto. Tocá «Reintentar ahora» o revisá el cable o el Wi-Fi de este puesto.");
  if (alive === null) throw new Error("No se pudo comprobar si el servidor contesta en su dirección: probá de nuevo en un momento.");
  if (!ident.serverDeviceId) throw new Error("Faltan los datos del equipo del servidor: el servidor tiene que mandar la copia al menos una vez con esta versión");
  // Un cliente aparte, sin guardar sesión: solo para comprobar al administrador.
  const { createClient } = await import("@supabase/supabase-js");
  const temp = createClient(env.supabaseUrl, env.supabaseAnonKey, { auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false } });
  const login = await temp.auth.signInWithPassword({ email: admin.email.trim(), password: admin.password });
  if (login.error || !login.data.session) throw new Error("El email o la contraseña no son de una cuenta del negocio");
  const { data, error } = await temp.rpc("lan_replace_server", { _business_id: dev.businessId, _old_device_id: ident.serverDeviceId, _new_device_id: dev.deviceId });
  await temp.auth.signOut({ scope: "local" }).catch(() => undefined);
  if (error) throw new Error(`No se pudo consultar la nube: ${error.message}`);
  if (typeof data === "string" && data) throw new Error(data);
  set({ state: "error", error: "Esta PC pasa a ser el servidor de la sucursal: se reinicia y baja la base de la nube…", code: "takeover" });
  await saveDevices(ident.devices.filter((v) => v.id !== pairing.deviceId));
  writeLanConfig({ role: "server", serverId: ident.srv, port: Number(ident.port) || 47810, backupDeviceId: null, gen: Number(ident.gen ?? 0) + 1 });
  await clearIdentity(ident.srv);
  await savePairing(null);
  await import("@/modules/push/push-desktop").then((m) => m.setDesktopAutostart(true)).catch(() => undefined);
  client?.close();
  setTimeout(() => window.location.reload(), 800);
}

/**
 * 1.99.19: ¿hay un servidor atendiendo en esa dirección? true: sí (cualquier respuesta, aunque no reconozca al puesto);
 * false: no hay nadie (no conecta o no contesta); null: no se sabe (se cortó en el medio).
 */
async function answersAt(srv: { host?: string; port?: number } | null | undefined): Promise<boolean | null> {
  if (!srv?.host) return false;
  const pairing = await loadPairing();
  const probe = connectOnce(srv.host, Number(srv.port) || 47810, pairing).then((c) => { c.close(); return true as boolean | null; }, (e) => {
    if (!(e instanceof LanError)) return false; // no se pudo conectar: no hay servidor ahí
    if (e.code === "closed") return null;
    return e.code === "timeout" ? false : true;
  });
  const late = new Promise<boolean | null>((r) => setTimeout(() => r(false), 6000));
  return Promise.race([probe, late]).catch(() => null);
}

/** Reintentar ya (botón de la pantalla «Sin servidor»). */
export function retryNow() {
  if (loop) clearTimeout(loop);
  attempt = Math.max(attempt, 2);
  void tick();
}

/**
 * Vincular este equipo como puesto del servidor `host:port`. Si sale bien, queda guardado (la app se
 * reinicia para trabajar con la base del servidor).
 */
export async function pairWith(host: string, port: number, mode: { mode: "ask" } | { mode: "code"; code: string }, onSas: (s: string) => void,
  confirmSas?: (s: string) => Promise<boolean>): Promise<{ serverName: string }> {
  const c = await connectOnce(host, port, null, mode, onSas, confirmSas);
  const p = await loadPairing();
  const cfg: LanConfig = { role: "puesto", server: { host, port, id: p?.serverId ?? c.server?.id ?? "", name: c.server?.name ?? host } };
  writeLanConfig(cfg);
  return { serverName: c.server?.name ?? host };
}

/** Dejar de ser puesto (vuelve a trabajar con su propia base y la nube). */
export async function leavePuesto() {
  client?.close();
  client = null;
  const p = await loadPairing();
  if (p) await import("./backup").then((b) => b.clearIdentity(p.serverId)).catch(() => undefined);
  await savePairing(null);
  writeLanConfig({ role: null });
}

/** 1.81.15: el equipo del servidor (su punto CAEA es el de la sucursal). */
let srvDevice: string | null = null;
export async function serverDeviceId(): Promise<string | null> {
  if (srvDevice) return srvDevice;
  const r = await serverCall<{ deviceId?: string }>("device").catch(() => null);
  srvDevice = r?.deviceId ?? null;
  return srvDevice;
}

/** Pedido simple al servidor (estado de sincronización, hora). */
export async function serverCall<T>(m: string, a?: unknown, timeoutMs?: number): Promise<T> {
  return (await getClient()).call<T>(m, a, timeoutMs);
}
